import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';

const AuthContext = createContext();

// Auth actions
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING',
  VERIFY_TOKEN: 'VERIFY_TOKEN'
};

// Initial state - start with no token check
const initialState = {
  user: null,
  token: null, // Don't get from localStorage initially
  featureConfig: {}, // Add feature configuration
  isAuthenticated: false,
  isAuthLoading: false,
  isLoading: true,
  error: null
};

// Auth reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        isAuthLoading: true,
        error: null
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        featureConfig: action.payload.featureConfig || {}, // Include feature config
        isAuthenticated: true,
        isLoading: false, // Explicitly set to false
        isAuthLoading: false,
        error: null
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        featureConfig: {}, // Clear feature config on failure
        isAuthenticated: false,
        isLoading: false,
        isAuthLoading: false,
        error: action.payload
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        featureConfig: {}, // Clear feature config on logout
        isAuthenticated: false,
        isLoading: false,
        isAuthLoading: false,
        error: null
      };

    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };

    case AUTH_ACTIONS.VERIFY_TOKEN:
      if (action.payload.success) {
        return {
          ...state,
          user: action.payload.user,
          token: action.payload.token, // Include token in state
          featureConfig: action.payload.featureConfig || {}, // Include feature config
          isAuthenticated: true,
          isLoading: false,
          isAuthLoading: false,
          error: null
        };
      } else {
        return {
          ...state,
          user: null,
          token: null,
          featureConfig: {}, // Clear feature config if verification fails
          isAuthenticated: false,
          isLoading: false,
          isAuthLoading: false,
          error: null
        };
      }

    default:
      return state;
  }
};

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // API base URL for hub-backend
  const API_BASE_URL = typeof window !== 'undefined'
    ? (window.location.hostname === 'localhost' ? 'http://localhost:3001' : '')
    : '';

  // Promise lock to prevent concurrent refresh calls
  const refreshPromiseRef = useRef(null);

  // Refresh access token using refresh token
  const refreshAccessToken = useCallback(async () => {
    // If a refresh is already in-flight, return the same promise
    if (refreshPromiseRef.current) return refreshPromiseRef.current;

    const doRefresh = async () => {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) return null;

      try {
        const fullUrl = `${API_BASE_URL}/api/auth/refresh`;
        const response = await fetch(fullUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });

        if (!response.ok) return null;

        const data = await response.json();
        if (data.success) {
          localStorage.setItem('authToken', data.data.token);
          localStorage.setItem('refreshToken', data.data.refreshToken);
          return data.data.token;
        }
        return null;
      } catch {
        return null;
      } finally {
        refreshPromiseRef.current = null;
      }
    };

    refreshPromiseRef.current = doRefresh();
    return refreshPromiseRef.current;
  }, [API_BASE_URL]);

  // API request helper with authentication
  // Note: reads token from localStorage (not state) so this callback stays stable
  // and doesn't trigger re-renders / verifyToken loops on every state change.
  const apiRequest = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem('authToken');

    // Prepend base URL if the URL doesn't have a protocol
    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;

    const config = {
      ...options,
      headers: {
        ...(options.headers || {}),
        'Content-Type': 'application/json',
        ...(token && !options.headers?.Authorization && {
          Authorization: `Bearer ${token}`,
        }),
      },
    };

    const response = await fetch(fullUrl, config);
    const data = await response.json();

    if (response.status === 401) {
      // Attempt token refresh before logging out
      const newToken = await refreshAccessToken();
      if (newToken) {
        config.headers.Authorization = `Bearer ${newToken}`;
        const retryResponse = await fetch(fullUrl, config);
        const retryData = await retryResponse.json();

        if (retryResponse.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
          throw new Error('Session expired. Please login again.');
        }

        return { response: retryResponse, data: retryData };
      }

      // Refresh failed — force logout
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      throw new Error('Session expired. Please login again.');
    }

    return { response, data };
  }, [API_BASE_URL, refreshAccessToken]);

  // Fetch feature configuration
  const fetchFeatureConfig = useCallback(async () => {
    try {
      const { data } = await apiRequest('/api/configuration/features');
      return data.success ? data.data : {};
    } catch (error) {
      console.warn('Failed to fetch feature configuration:', error);
      return {}; // Return empty config if fetch fails
    }
  }, [apiRequest]);

  // Login function
  const login = async (username, password, options = {}) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const { data } = await apiRequest('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          username: username.toLowerCase(),
          password,
          acceptedTerms: options.acceptedTerms === true,
        })
      });

      if (data.success) {
        localStorage.setItem('authToken', data.data.token);
        if (data.data.refreshToken) {
          localStorage.setItem('refreshToken', data.data.refreshToken);
        }

        // Fetch feature configuration after successful login
        const featureConfig = await fetchFeatureConfig();
        
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: {
            user: data.data.user,
            token: data.data.token,
            featureConfig: featureConfig
          }
        });
        return { success: true };
      } else {
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: data.error || 'Login failed'
        });
        return { success: false, error: data.error };
      }
    } catch (error) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: error.message || 'Network error'
      });
      return { success: false, error: error.message };
    }
  };

  // Logout function
  const logout = async () => {
    // Try to revoke refresh token server-side
    const refreshToken = localStorage.getItem('refreshToken');
    const token = state.token || localStorage.getItem('authToken');
    if (refreshToken && token) {
      fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ refreshToken }),
      }).catch(() => {}); // Best-effort, don't block logout
    }

    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  };

  // Verify token on app load
  const verifyToken = useCallback(async () => {
    const token = localStorage.getItem('authToken');

    if (!token) {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      return;
    }

    try {
      const { data } = await apiRequest('/api/auth/verify');

      if (data.success) {
        // Fetch feature configuration after successful token verification
        const featureConfig = await fetchFeatureConfig();

        dispatch({
          type: AUTH_ACTIONS.VERIFY_TOKEN,
          payload: {
            success: true,
            user: data.data?.user,
            token: token,
            featureConfig: featureConfig
          }
        });
      } else {
        dispatch({
          type: AUTH_ACTIONS.VERIFY_TOKEN,
          payload: { success: false }
        });
      }
    } catch (error) {
      console.warn('Token verification failed, attempting refresh...');

      // Attempt refresh before giving up
      const newToken = await refreshAccessToken();
      if (newToken) {
        try {
          const fullUrl = `${API_BASE_URL}/api/auth/verify`;
          const retryResponse = await fetch(fullUrl, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${newToken}`,
            },
          });
          const retryData = await retryResponse.json();

          if (retryData.success) {
            const featureConfig = await fetchFeatureConfig();
            dispatch({
              type: AUTH_ACTIONS.VERIFY_TOKEN,
              payload: { success: true, user: retryData.data?.user, token: newToken, featureConfig }
            });
            return;
          }
        } catch {
          // Fall through to logout
        }
      }

      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      dispatch({
        type: AUTH_ACTIONS.VERIFY_TOKEN,
        payload: { success: false }
      });
    }
  }, [apiRequest, fetchFeatureConfig, refreshAccessToken, API_BASE_URL]);

  // Helper function to check if a feature is enabled
  const isFeatureEnabled = (featureName) => {
    return state.featureConfig[featureName] === true;
  };

  // Helper function to get feature configuration
  const getFeatureConfig = (featureName) => {
    return state.featureConfig[featureName];
  };

  // Other functions remain the same...
  const changePassword = async (currentPassword, newPassword, confirmPassword) => {
    try {
      const { data } = await apiRequest('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({
          currentPassword,
          newPassword,
          confirmPassword
        })
      });

      return {
        success: data.success,
        message: data.message,
        error: data.error
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to change password'
      };
    }
  };

  const getCurrentUser = async () => {
    try {
      const { data } = await apiRequest('/api/auth/me');
      
      if (data.success) {
        return { success: true, user: data.data.user };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const getAllUsers = async () => {
    try {
      const { data } = await apiRequest('/api/auth/users');
      if (data.success) {
        return { success: true, users: data.data.users };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const createUser = async (username, password, role = 'user') => {
    try {
      const { data } = await apiRequest('/api/auth/users', {
        method: 'POST',
        body: JSON.stringify({ username: username.toLowerCase(), password, role })
      });
      if (data.success) {
        return { success: true, user: data.data.user };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateUser = async (userId, updates) => {
    try {
      const { data } = await apiRequest(`/api/auth/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      if (data.success) {
        return { success: true, user: data.data.user };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const deleteUser = async (userId) => {
    try {
      const { data } = await apiRequest(`/api/auth/users/${userId}`, {
        method: 'DELETE'
      });
      if (data.success) {
        return { success: true, message: data.message };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // ===== API Tokens (mobile app tokens) =====
  const listApiTokens = async () => {
    try {
      const { data } = await apiRequest('/api/auth/api-tokens');
      if (data.success) {
        return { success: true, tokens: data.data.tokens };
      }
      return { success: false, error: data.error };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const createApiToken = async (name, role = 'user') => {
    try {
      const { data } = await apiRequest('/api/auth/api-tokens', {
        method: 'POST',
        body: JSON.stringify({ name, role })
      });
      if (data.success) {
        return { success: true, ...data.data };
      }
      return { success: false, error: data.error };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const revokeApiToken = async (tokenId) => {
    try {
      const { data } = await apiRequest(`/api/auth/api-tokens/${tokenId}`, {
        method: 'DELETE'
      });
      if (data.success) {
        return { success: true };
      }
      return { success: false, error: data.error };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const isAdmin = () => state.user?.role === 'admin';

  // Only verify token on mount, not on every state change
  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const value = {
    ...state,
    login,
    logout,
    changePassword,
    getCurrentUser,
    getAllUsers,
    createUser,
    updateUser,
    deleteUser,
    listApiTokens,
    createApiToken,
    revokeApiToken,
    isAdmin,
    apiRequest,
    verifyToken,
    isFeatureEnabled,
    getFeatureConfig
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export default AuthContext;